// ── Sprint board page (scrum-style grouped by priority) ──

import { api } from '../api.js';
import { getUser } from '../auth.js';
import { showToast, closeModal } from '../ui.js';
import { escapeHtml, groupBySprint, renderAvatarHtml, renderAgentBadge, renderDuePill, canStartTask, availableStatuses } from '../utils.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { showAssigneePicker, removeAssigneePicker } from '../components/assignee-picker.js';
import { statusNames, statusLabel, statusBadgeAttrs, roleStatus, isClosedStatus, closedNames } from '../lib/statuses.js';
import { findInsertBefore, dropIndex } from '../lib/drag-order.js';
import { groupLaneTitle } from '../lib/task-group-label.js';

const DRAG_THRESHOLD = 5;

// Module-scoped, keyed by projectId — deliberately survives resetState(). board.js rebuilds
// this sub-view on every filter change and (C1261) on every task-detail drawer close, so a
// state-local Set would drop the user's expanded/collapsed lanes each time — including
// re-collapsing an all-done lane (auto-collapsed by default, see renderLane()) the user had
// just expanded in order to click into it.
let collapsedLanes = new Set();
let collapsedLanesProjectId = null;

let state = {
  projectId: null,
  tasks: [],
  members: [],
  sprints: [],
  statuses: [],
  collapsedLanes: new Set(),
  drag: null,
  filtersActive: false,
  groupLabel: 'Sprint', // singular noun for lane title bars (C1234, project's task_group_label)
  onDataChanged: null, // bubbles "server data changed" up to board.js (C1261) — see reloadTasks()
  onAddTask: null, // opens board.js's New Task modal, prefilled with a lane's sprint (TPT30)
  sprintsEnabled: true, // C1334 — false → flat render, no lanes
};

function resetState() {
  state = { projectId: null, tasks: [], members: [], sprints: [], statuses: [], collapsedLanes: new Set(), drag: null, filtersActive: false, groupLabel: 'Sprint', onDataChanged: null, onAddTask: null, sprintsEnabled: true };
}

// Bubble a data-changing event (drawer close, drag-save failure) up to
// board.js's own reload rather than refetching into state.tasks here — state.tasks IS
// board.js's `filtered` array (applyFilters output), a throwaway once any filter is active
// (C1261). Resolved from state at call time, not captured, so a late callback (task-detail's
// onClose fires ~220ms after close) that lands after this sub-view tore down correctly no-ops
// instead of stomping a array that's no longer on screen.
async function reloadTasks() {
  if (state.onDataChanged) {
    await state.onDataChanged();
    return;
  }
  // No owner callback wired (shouldn't happen — board.js always passes one) — fall back to
  // a local repaint so this never silently no-ops.
  renderBoard();
}

// Role-based "done" check (C1182, closed via the complete OR canceled role as of C1187).
function isDoneStatus(name) {
  return isClosedStatus(state.statuses, name);
}

// ── Rendering ──

function renderProgressBar(sprint) {
  const done = sprint.tasks.filter(t => isDoneStatus(t.status)).length;
  const total = sprint.tasks.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return `<div class="sprint-progress"><div class="sprint-progress-fill" style="width:${pct}%"></div></div>
    <span class="sprint-lane-pct text-faint text-sm">${pct}%</span>`;
}

function renderCard(task) {
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  const tags = (task.tags || []).slice(0, 3);
  const extraTags = (task.tags || []).length - 3;

  let html = `<div class="sb-card" data-task-key="${escapeHtml(task.task_key)}" data-priority="${task.priority}">`;
  html += `<div class="sb-card-header">`;
  html += `<a class="badge ${catClass} task-key-link" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`;
  const _statusRow = state.statuses.find(s => s.name === task.status);
  const _pillAttrs = statusBadgeAttrs(_statusRow);
  html += `<span class="sb-status-pill ${_pillAttrs.className}" style="${_pillAttrs.style}" data-task-key="${escapeHtml(task.task_key)}">${escapeHtml(statusLabel(task.status))}</span>`;
  html += `</div>`;
  html += `<div class="sb-card-title">${escapeHtml(task.title)}</div>`;
  const desc = (task.description || '').trim();
  if (desc) {
    const trunc = desc.length > 120 ? desc.slice(0, 117) + '...' : desc;
    html += `<div class="sb-card-description text-muted">${escapeHtml(trunc)}</div>`;
  }
  html += renderDuePill(task, closedNames(state.statuses));
  const assigneeUser = task.assignee ? state.members.find(m => (m.user_id || m.id) === task.assignee) : null;
  html += `<div class="sb-card-footer" style="display:flex;align-items:center;gap:0.5rem;">`;
  if (tags.length) {
    html += `<div class="sb-card-tags" style="flex:1;min-width:0;">`;
    html += tags.map(t => `<span class="tag">${escapeHtml(t)}</span>`).join('');
    if (extraTags > 0) html += `<span class="text-faint" style="font-size:0.7rem"> +${extraTags}</span>`;
    html += `</div>`;
  } else {
    html += `<div style="flex:1"></div>`;
  }
  html += `<div class="card-avatar-wrap" data-task-key="${escapeHtml(task.task_key)}" data-action="assign">`;
  html += renderAvatarHtml(assigneeUser, 24);
  html += renderAgentBadge(task, state.members, { overlap: true });
  html += `</div>`;
  html += `</div></div>`;
  return html;
}

function renderLane(sprint) {
  const isAutoCollapsed = sprint.allDone;
  const isManuallyToggled = state.collapsedLanes.has(sprint.priority);
  const isCollapsed = isAutoCollapsed ? !isManuallyToggled : isManuallyToggled;
  const remaining = sprint.tasks.filter(t => !isDoneStatus(t.status)).length;

  // (C1395) A stored sprint-row name that's just the auto-generated boilerplate ("Sprint N",
  // written by every sprint-creating writer regardless of the project's task_group_label)
  // isn't a custom name — fall back to the project's own noun instead of echoing it verbatim.
  const title = escapeHtml(groupLaneTitle(sprint.sprintNumber, sprint, state.groupLabel));
  let html = `<div class="sprint-lane ${isCollapsed ? 'sprint-lane-collapsed' : ''}" data-priority="${sprint.priority}">`;
  html += `<div class="sprint-lane-header" data-priority="${sprint.priority}">`;
  html += `<span class="sprint-lane-chevron">&#9660;</span>`;
  html += `<span class="sprint-lane-title">${title}</span>`;
  if (sprint.sprintStatus === 'closed') html += `<span class="badge badge-canceled">Closed</span>`;
  html += `<span class="badge ${remaining > 0 ? 'badge-in_progress' : 'badge-completed'}">${remaining > 0 ? remaining + ' remaining' : 'all done'}</span>`;
  html += renderProgressBar(sprint);
  html += `</div>`;
  if (!isCollapsed && sprint.goal) {
    html += `<div class="sprint-lane-goal">${escapeHtml(sprint.goal)}</div>`;
  }

  if (!isCollapsed) {
    html += `<div class="sprint-cards" data-priority="${sprint.priority}">`;
    const sorted = [...sprint.tasks].sort((a, b) => b.display_order - a.display_order);
    html += sorted.map(renderCard).join('');
    html += `<button type="button" class="sb-add-tile" data-priority="${sprint.priority}">+ Add item</button>`;
    html += `</div>`;
  }
  html += `</div>`;
  return html;
}

// C1334 — flat render path. One `.sprint-cards` grid, no lane chrome: renderLane()/
// renderProgressBar()/the chevron are simply not called and groupBySprint() never runs.
// Reuses `.sprint-cards` so no new CSS is needed (it already carries the card grid and
// the .drag-over state); `.sprint-flat` is an identity marker and the drag code's
// flat-mode signal.
//
// Deliberately carries NO data-priority — sprint membership lives on each card's own
// data-priority (renderCard already emits it), which is what the drop handler reads. The
// trailing `.sb-add-tile` also carries no data-priority here (unlike renderLane()'s), so
// bindBoardEvents() falls through to board.js's own default sprint when it's clicked.
//
// Sort = priority DESC, display_order DESC: byte-for-byte the sequence the laned view
// produces (groupBySprint sorts priorities desc, renderLane sorts each lane by
// display_order desc). Flipping the toggle never reorders anything.
// Sorting a COPY is mandatory — state.tasks here is board.js's `filtered` array, which IS
// state.tasks when no filter is active (C1261 identity rule); an in-place sort would
// silently reorder the page owner's list.
function renderFlatList(tasks) {
  const sorted = [...tasks].sort(
    (a, b) => (b.priority - a.priority) || (b.display_order - a.display_order)
  );
  return '<div class="sprint-cards sprint-flat">'
    + sorted.map(renderCard).join('')
    + '<button type="button" class="sb-add-tile">+ Add item</button>'
    + '</div>';
}

function renderBoard() {
  const board = document.getElementById('sprint-board');
  if (!board) return;

  if (state.tasks.length === 0) {
    board.innerHTML = '<div class="empty">No tasks yet.</div>';
    return;
  }

  if (!state.sprintsEnabled) {
    board.innerHTML = renderFlatList(state.tasks);
    return;
  }

  const sprintGroups = groupBySprint(state.tasks, closedNames(state.statuses));

  // Enrich with sprint metadata
  for (const group of sprintGroups) {
    const meta = state.sprints.find(s => s.number === group.priority);
    if (meta) {
      group.name = meta.name;
      group.sprintStatus = meta.status;
      group.goal = meta.goal;
    }
  }

  // Closed sprints render whenever they're in the shared sprint selector's
  // selection (dimmed there, still tickable) — no second closed-lane filter
  // here (C1166 follow-up: the "Show closed sprints" toggle was redundant
  // with that selector and has been removed).
  let visible = sprintGroups;

  // Hide empty lanes when filters active
  if (state.filtersActive) {
    visible = visible.filter(s => s.tasks.length > 0);
  }

  board.innerHTML = visible.map(renderLane).join('');
}

// ── Status pill dropdown ──

function removeStatusDropdown() {
  document.querySelector('.sb-status-dropdown')?.remove();
}

function showStatusDropdown(pill) {
  removeStatusDropdown();
  const taskKey = pill.dataset.taskKey;
  const rect = pill.getBoundingClientRect();

  const task = state.tasks.find(t => t.task_key === taskKey);
  const names = statusNames(state.statuses);
  const inProgressName = roleStatus(state.statuses, 'in_progress')?.name;
  const opts = task ? availableStatuses(names, task, getUser(), inProgressName) : names;
  const dropdown = document.createElement('div');
  dropdown.className = 'sb-status-dropdown';
  dropdown.style.cssText = `position:fixed;top:${rect.bottom + 4}px;left:${rect.left}px;z-index:250;`;
  dropdown.innerHTML = opts.map(s => {
    const attrs = statusBadgeAttrs(state.statuses.find(row => row.name === s));
    return `<div class="sb-status-option ${attrs.className}" style="${attrs.style}" data-status="${escapeHtml(s)}" data-task-key="${escapeHtml(taskKey)}">${escapeHtml(statusLabel(s))}</div>`;
  }).join('');
  document.body.appendChild(dropdown);

  setTimeout(() => {
    const handler = (ev) => {
      if (!dropdown.contains(ev.target)) {
        dropdown.remove();
        document.removeEventListener('mousedown', handler);
      }
    };
    document.addEventListener('mousedown', handler);
  }, 0);
}

async function updateStatus(taskKey, newStatus) {
  const task = state.tasks.find(t => t.task_key === taskKey);
  if (!task || task.status === newStatus) return;

  // Block moving another member's task onto the in-progress-role status
  const inProgressName = roleStatus(state.statuses, 'in_progress')?.name;
  if (newStatus === inProgressName && !canStartTask(task, getUser())) {
    showToast('Cannot start a task assigned to another member', 'error');
    return;
  }

  const oldStatus = task.status;
  task.status = newStatus;
  renderBoard();

  try {
    const { task: updated } = await api.patch(`/projects/${state.projectId}/tasks/${taskKey}`, { status: newStatus });
    const idx = state.tasks.findIndex(t => t.task_key === taskKey);
    if (idx >= 0) state.tasks[idx] = updated;
    renderBoard();
  } catch (err) {
    task.status = oldStatus;
    renderBoard();
    showToast(err.message || 'Failed to update status', 'error');
  }
}

// ── Task detail (slide-out panel) ──

function openTaskDetailPanel(taskKey) {
  openTaskDetail(state.projectId, taskKey, {
    allTasks: state.tasks,
    onClose: reloadTasks,
  });
}

// ── Drag and drop (mouse-based, same pattern as kanban) ──

let onMouseDown, onMouseMove, onMouseUp;

function bindDragAndDrop() {
  onMouseDown = (e) => {
    const card = e.target.closest('.sb-card');
    if (!card) return;
    if (e.target.closest('.sb-status-pill, select, button, a, input, textarea, [contenteditable]')) return;

    // Avatar click → assignee picker, no drag
    const avatarWrap = e.target.closest('.card-avatar-wrap');
    if (avatarWrap) {
      e.preventDefault();
      const taskKey = avatarWrap.dataset.taskKey;
      showAssigneePicker(avatarWrap, {
        members: state.members,
        currentAssignee: state.tasks.find(t => t.task_key === taskKey)?.assignee,
        onSelect: (userId) => handleAssign(taskKey, userId),
      });
      return;
    }

    const rect = card.getBoundingClientRect();
    state.drag = {
      taskKey: card.dataset.taskKey,
      el: card,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onMouseMove = (e) => {
    const d = state.drag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('sb-card-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      document.body.appendChild(d.clone);

      d.placeholder = document.createElement('div');
      d.placeholder.className = 'sb-card-placeholder';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      d.el.classList.add('sb-card-hidden');
    }

    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (!target) return;
    const cardsContainer = target.closest('.sprint-cards');

    document.querySelectorAll('.sprint-cards.drag-over').forEach(el => el.classList.remove('drag-over'));

    if (!cardsContainer) return;
    cardsContainer.classList.add('drag-over');

    // 'grid' — .sprint-cards is a multi-column CSS grid, not a single vertical list
    // (styles.css .sprint-cards { grid-template-columns: repeat(auto-fill, ...) }):
    // a Y-only hit-test always matches a row's first card, so the placeholder could
    // only ever land at a row boundary. findInsertBefore('grid') picks the row via Y
    // then the slot within it via X (degrading to plain top/bottom-half once the grid
    // has collapsed to one column on narrow viewports) — see lib/drag-order.js.
    const cards = [...cardsContainer.querySelectorAll('.sb-card:not(.sb-card-hidden)')];
    const insertBefore = findInsertBefore(cards, e.clientX, e.clientY, 'grid');

    if (insertBefore) {
      cardsContainer.insertBefore(d.placeholder, insertBefore);
    } else {
      // Append before the trailing add-item tile (if present) rather than as the
      // container's last child — appendChild would otherwise drop the placeholder into
      // a new row *under* the "+ Add item" tile and dropIndex() would then count the
      // tile as a preceding sibling of the drop position.
      const addTile = cardsContainer.querySelector('.sb-add-tile');
      if (addTile) {
        cardsContainer.insertBefore(d.placeholder, addTile);
      } else {
        cardsContainer.appendChild(d.placeholder);
      }
    }
  };

  onMouseUp = async (e) => {
    const d = state.drag;
    if (!d) return;

    document.querySelectorAll('.sprint-cards.drag-over').forEach(el => el.classList.remove('drag-over'));

    if (!d.moved) {
      // Was a click — open detail panel
      state.drag = null;
      openTaskDetailPanel(d.taskKey);
      return;
    }

    const cardsContainer = d.placeholder?.closest('.sprint-cards');
    const isFlat = !!cardsContainer && cardsContainer.classList.contains('sprint-flat');
    const task = state.tasks.find(t => t.task_key === d.taskKey);

    let targetPriority;
    if (isFlat) {
      // C1334 — no lanes, so the drop position itself names the sprint: the nearest VISIBLE
      // card above the placeholder wins (dragging a card up past an invisible boundary
      // promotes it into that sprint, exactly like dropping into a higher lane). Above every
      // card → fall through to the nearest card below, i.e. the topmost sprint on the board.
      // A lone card with no neighbours keeps its own sprint.
      const neighbourPriority = (from, dir) => {
        let el = from && from[dir];
        while (el) {
          if (el.classList?.contains('sb-card') && !el.classList.contains('sb-card-hidden')) {
            return parseInt(el.dataset.priority, 10);
          }
          el = el[dir];
        }
        return NaN;
      };
      const above = neighbourPriority(d.placeholder, 'previousElementSibling');
      const below = neighbourPriority(d.placeholder, 'nextElementSibling');
      targetPriority = Number.isFinite(above) ? above
        : (Number.isFinite(below) ? below : (task ? task.priority : NaN));
    } else {
      targetPriority = cardsContainer ? parseInt(cardsContainer.dataset.priority, 10) : NaN;
    }

    // Must run BEFORE the cleanup below removes 'sb-card-hidden' from d.el — dropIndex()
    // counts elements NOT carrying that class, so a hidden-then-unhidden dragged original
    // would otherwise start counting itself (see lib/drag-order.js). Flat mode further
    // restricts the count to cards already at targetPriority, mirroring the old inline
    // filter this replaces.
    const dropAt = cardsContainer
      ? dropIndex(cardsContainer, d.placeholder, {
          selector: '.sb-card',
          hiddenClass: 'sb-card-hidden',
          filter: isFlat ? (el) => parseInt(el.dataset.priority, 10) === targetPriority : null,
        })
      : 0;

    // Cleanup DOM
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('sb-card-hidden');

    state.drag = null;

    // `parseInt(undefined, 10)` is NaN, and `NaN === null` is false — without this check a
    // cards container with no data-priority (i.e. the C1334 flat grid, pre-neighbour-lookup)
    // would fall through and PATCH `priority: NaN`, which JSON.stringify emits as `null`.
    if (!task || !Number.isFinite(targetPriority)) return;

    // Build new ordered list for target lane (the target SPRINT's own tasks, not the whole
    // visible list — bounds the PATCH set to that sprint's size even in flat mode).
    const laneTasks = state.tasks
      .filter(t => t.priority === targetPriority && t.task_key !== task.task_key)
      .sort((a, b) => b.display_order - a.display_order);

    const insertAt = Math.min(dropAt, laneTasks.length);
    laneTasks.splice(insertAt, 0, task);

    // Calculate new display_order values
    const patches = [];
    laneTasks.forEach((t, i) => {
      const newOrder = (laneTasks.length - i) * 10;
      const priorityChanged = t.task_key === task.task_key && t.priority !== targetPriority;
      const orderChanged = t.display_order !== newOrder;
      if (priorityChanged || orderChanged) {
        const patch = { display_order: newOrder };
        if (priorityChanged) patch.priority = targetPriority;
        patches.push({ taskKey: t.task_key, patch });
      }
    });

    if (patches.length === 0) return;

    // Optimistic update
    task.priority = targetPriority;
    laneTasks.forEach((t, i) => { t.display_order = (laneTasks.length - i) * 10; });
    renderBoard();

    try {
      const results = await Promise.all(patches.map(({ taskKey, patch }) =>
        api.patch(`/projects/${state.projectId}/tasks/${taskKey}`, patch)
      ));
      // C1464 — dropping an objective parent into a sprint can cascade its children
      // (and its own priority) beyond this drag's own optimistic update — reload
      // through the owner so the board reflects the server-computed schedule.
      // TPT3 — the reverse direction: dropping a SUBTASK can pull its own objective
      // parent along (parent_rescheduled) via the TPT2 cascade. Same reload, since this
      // page has no client-side regroup helper of its own — a full refetch is how it
      // reflects any server-side move.
      if (results.some(r => r && (r.children_rescheduled || r.parent_rescheduled))) {
        await reloadTasks();
      }
    } catch (err) {
      showToast('Failed to save order', 'error');
      // Server is the truth now — re-derive everything through the owner rather than
      // splicing an unfiltered refetch into this (possibly filtered) array (C1261).
      await reloadTasks();
    }
  };

  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

async function handleAssign(taskKey, userId) {
  const task = state.tasks.find(t => t.task_key === taskKey);
  if (!task) return;
  const old = task.assignee;
  task.assignee = userId;
  renderBoard();
  try {
    await api.patch('/projects/' + state.projectId + '/tasks/' + taskKey, { assignee: userId });
  } catch (err) {
    task.assignee = old;
    renderBoard();
    showToast('Failed to assign', 'error');
  }
}

function cleanupDrag() {
  if (state.drag) {
    if (state.drag.clone) state.drag.clone.remove();
    if (state.drag.placeholder) state.drag.placeholder.remove();
    if (state.drag.el) state.drag.el.classList.remove('sb-card-hidden');
    state.drag = null;
  }
  document.querySelectorAll('.sprint-cards.drag-over').forEach(el => el.classList.remove('drag-over'));
  if (onMouseDown) document.removeEventListener('mousedown', onMouseDown);
  if (onMouseMove) document.removeEventListener('mousemove', onMouseMove);
  if (onMouseUp) document.removeEventListener('mouseup', onMouseUp);
}

// ── Board events (delegation) ──

function bindBoardEvents() {
  const board = document.getElementById('sprint-board');
  if (!board) return;

  board.addEventListener('click', (e) => {
    // Card-shaped "+ Add item" tile (TPT30) — opens board.js's New Task modal, prefilled
    // with this lane's sprint. The flat-mode tile carries no data-priority; parseInt(undefined)
    // is NaN, so the modal falls back to its own default sprint instead of receiving one.
    const addTile = e.target.closest('.sb-add-tile');
    if (addTile) {
      if (state.onAddTask) {
        const priority = parseInt(addTile.dataset.priority, 10);
        state.onAddTask(Number.isFinite(priority) ? priority : undefined);
      }
      return;
    }

    // Sprint lane header toggle
    const header = e.target.closest('.sprint-lane-header');
    if (header) {
      const priority = parseInt(header.dataset.priority, 10);
      if (state.collapsedLanes.has(priority)) {
        state.collapsedLanes.delete(priority);
      } else {
        state.collapsedLanes.add(priority);
      }
      renderBoard();
      return;
    }

    // Status pill click
    const pill = e.target.closest('.sb-status-pill');
    if (pill) {
      showStatusDropdown(pill);
      return;
    }

    // Status dropdown option
    const option = e.target.closest('.sb-status-option');
    if (option) {
      updateStatus(option.dataset.taskKey, option.dataset.status);
      removeStatusDropdown();
      return;
    }
  });
}

// ── Sub-view render (used by board container) ──

export function renderSprintView(container, tasks, projectId, members, sprints, options, statuses) {
  resetState();
  state.projectId = projectId;
  state.tasks = tasks;
  state.members = members || [];
  state.sprints = sprints || [];
  state.statuses = statuses || [];
  state.filtersActive = (options && options.filtersActive) || false;
  state.groupLabel = (options && options.groupLabel) || 'Sprint';
  state.onDataChanged = (options && options.onDataChanged) || null;
  state.onAddTask = (options && options.onAddTask) || null;
  // C1334 — default TRUE so an options object from a caller that predates this flag keeps
  // rendering lanes.
  state.sprintsEnabled = !(options && options.sprintsEnabled === false);
  // Reset the persisted collapse set only on a genuine project switch — every other mount
  // (filter change, drawer close) should keep the user's expand/collapse choices (see the
  // module-scoped collapsedLanes comment above).
  if (collapsedLanesProjectId !== projectId) {
    collapsedLanes = new Set();
    collapsedLanesProjectId = projectId;
  }
  state.collapsedLanes = collapsedLanes;
  container.innerHTML = '<div id="sprint-board"></div>';
  renderBoard();
  bindBoardEvents();
  bindDragAndDrop();

  return () => {
    removeAssigneePicker();
    closeTaskDetail();
    removeStatusDropdown();
    cleanupDrag();
    closeModal();
    resetState();
  };
}
